import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Hash, BookOpen, ChevronRight, Volume2, Pause, Play } from 'lucide-react';
import { getItemByNumber, getStructure } from '../services/api';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import './ItemDetail.css';

const ItemDetail = () => {
  const { number } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setStructure] = useState(null);
  const { isPlaying, isPaused, playAudio, cleanupAudio } = useAudioPlayer();

  const itemNumber = parseInt(number, 10);

  const findItemLocation = useCallback((data, num) => {
    const parseRange = (rangeStr) => {
      if (!rangeStr) return null;
      if (rangeStr.includes('-')) {
        const [start, end] = rangeStr.split('-').map(n => parseInt(n.trim(), 10));
        return { start, end };
      } else {
        const n = parseInt(rangeStr, 10);
        return { start: n, end: n };
      }
    };

    for (const part of data.parts) {
      for (const chapter of part.chapters) {
        if (chapter.subsections) {
          for (const subsection of chapter.subsections) {
            const range = parseRange(subsection.item_range);
            if (range && num >= range.start && num <= range.end) {
              return {
                partId: part.id,
                partName: part.name,
                chapterTitle: chapter.title,
                subsectionTitle: subsection.title,
              };
            }
          }
        }
        if (chapter.sections) {
          for (const section of chapter.sections) {
            if (section.subsections) {
              for (const subsection of section.subsections) {
                const range = parseRange(subsection.item_range);
                if (range && num >= range.start && num <= range.end) {
                  return {
                    partId: part.id,
                    partName: part.name,
                    chapterTitle: `${chapter.title} - ${section.title}`,
                    subsectionTitle: subsection.title,
                  };
                }
              }
            }
            const range = parseRange(section.item_range);
            if (range && num >= range.start && num <= range.end) {
              return {
                partId: part.id,
                partName: part.name,
                chapterTitle: `${chapter.title} - ${section.title}`,
              };
            }
          }
        }
      }
    }
    return null;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [itemData, structureData] = await Promise.all([
          getItemByNumber(itemNumber),
          getStructure(),
        ]);
        
        setItem(itemData);
        setStructure(structureData);
        
        // 查找条目位置
        const loc = findItemLocation(structureData, itemNumber);
        setLocation(loc);
      } catch (error) {
        console.error('Error fetching item:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [itemNumber, findItemLocation]);



  const goToPrev = useCallback(() => {
    if (itemNumber > 1) {
      navigate(`/item/${itemNumber - 1}`);
    }
  }, [itemNumber, navigate]);

  const goToNext = useCallback(() => {
    if (itemNumber < 600) {
      navigate(`/item/${itemNumber + 1}`);
    }
  }, [itemNumber, navigate]);

  // 键盘导航
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrev();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrev, goToNext]);

  // 切换条目时停止播放
  useEffect(() => {
    cleanupAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemNumber]);

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>加载中...</p>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="error">
        <div className="error-icon">😕</div>
        <h2 className="error-message">未找到第 {number} 条</h2>
        <Link to="/" className="btn btn-primary">返回首页</Link>
      </div>
    );
  }

  return (
    <div className="item-detail-page">
      <div className="container">
        {/* Navigation */}
        <div className="detail-nav">
          <Link to="/chapters" className="back-link">
            <ArrowLeft size={18} />
            <span>返回章节</span>
          </Link>
        </div>

        {/* Breadcrumb */}
        {location && (
          <nav className="detail-breadcrumb">
            <Link to="/">首页</Link>
            <ChevronRight size={14} />
            <Link to={`/chapters?part=${location.partId}`}>{location.partName}</Link>
            <ChevronRight size={14} />
            <span>{location.chapterTitle}</span>
            {location.subsectionTitle && (
              <>
                <ChevronRight size={14} />
                <span>{location.subsectionTitle}</span>
              </>
            )}
          </nav>
        )}

        {/* Main Content */}
        <div className="detail-card animate-fade-in">
          <div className="detail-header">
            <div className="detail-number">
              <Hash size={20} />
              <span>{item.number}</span>
              <button 
                className={`detail-play-btn ${isPlaying ? 'playing' : ''} ${isPaused ? 'paused' : ''}`}
                onClick={() => playAudio(item.content)}
                title={isPlaying && !isPaused ? '暂停播放' : isPlaying && isPaused ? '继续播放' : '播放语音'}
                disabled={!item}
              >
                {isPlaying && !isPaused ? <Pause size={18} /> : isPlaying && isPaused ? <Play size={18} /> : <Volume2 size={18} />}
              </button>
            </div>
            <div className="detail-nav-buttons">
              <button 
                className="nav-btn" 
                onClick={goToPrev}
                disabled={itemNumber <= 1}
                title="上一条 (←)"
              >
                <ArrowLeft size={20} />
              </button>
              <button 
                className="nav-btn" 
                onClick={goToNext}
                disabled={itemNumber >= 600}
                title="下一条 (→)"
              >
                <ArrowRight size={20} />
              </button>
            </div>
          </div>

          <div className="detail-content">
            <p className="detail-text">{item.content}</p>
          </div>

          <div className="detail-footer">
            <div className="detail-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${(item.number / 600) * 100}%` }}
                ></div>
              </div>
              <span className="progress-text">{item.number} / 600</span>
            </div>
          </div>
        </div>

        {/* Context */}
        {location && (
          <div className="detail-context animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <h3 className="context-title">
              <BookOpen size={18} />
              所属章节
            </h3>
            <div className="context-info">
              <div className="context-item">
                <span className="context-label">篇章</span>
                <span className="context-value">{location.partName}</span>
              </div>
              <div className="context-item">
                <span className="context-label">章节</span>
                <span className="context-value">{location.chapterTitle}</span>
              </div>
              {location.subsectionTitle && (
                <div className="context-item">
                  <span className="context-label">小节</span>
                  <span className="context-value">{location.subsectionTitle}</span>
                </div>
              )}
            </div>
            <Link 
              to={`/chapters?part=${location.partId}`}
              className="context-link"
            >
              查看该章节全部内容
              <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {/* Related Items */}
        <div className="related-items animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <h3 className="related-title">相邻条目</h3>
          <div className="related-grid">
            {itemNumber > 1 && (
              <Link to={`/item/${itemNumber - 1}`} className="related-card prev">
                <span className="related-label">上一条</span>
                <span className="related-number">第 {itemNumber - 1} 条</span>
              </Link>
            )}
            {itemNumber < 600 && (
              <Link to={`/item/${itemNumber + 1}`} className="related-card next">
                <span className="related-label">下一条</span>
                <span className="related-number">第 {itemNumber + 1} 条</span>
              </Link>
            )}
          </div>
        </div>

        {/* Keyboard Hint */}
        <div className="keyboard-hint">
          <p>💡 提示：可以使用键盘 ← → 方向键快速切换条目</p>
        </div>
      </div>
    </div>
  );
};

export default ItemDetail;
